import {
  forwardRef,
  useMemo,
  useState,
  type ForwardedRef,
  type ChangeEvent,
} from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { Checkbox } from "../checkbox";
import { TextInput } from "../text-input";

import { Dropdown } from "./dropdown";
import { OptionItem } from "./option-item";
import { SelectButton } from "./select-button";
import { type BaseProps, type Option } from "./types";

const SIZE_TO_HEIGHT = { sm: 32, md: 36, lg: 40 } as const;
const SELECT_ALL_VALUE = "__select_all";

interface Props<T> extends BaseProps<T> {
  values: T[];
  onChange: (val: T[]) => void;
  hasSelectAll?: boolean;
  hasSearch?: boolean;
}

const _MultiSelect = <T,>(
  {
    className,
    values,
    options,
    placeholder,
    disabled,
    clearable,
    size = "sm",
    hideCaret,
    buttonClassName,
    dropdownClassName,
    itemClassName,
    onChange,
    hasSelectAll,
    hasSearch,
  }: Props<T>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const [scrollRef, setScrollRef] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [options, searchQuery]);

  const nonDisabledOptions = useMemo(() => {
    return filteredOptions.filter((e) => !e.disabled);
  }, [filteredOptions]);

  const selectedOptions = useMemo(() => {
    return options.filter((e) => values.includes(e.value));
  }, [options, values]);

  const selectedValues = useMemo(() => {
    return selectedOptions.map((e) => e.value);
  }, [selectedOptions]);

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    paddingStart: hasSelectAll ? SIZE_TO_HEIGHT[size] : 0,
    getScrollElement: () => scrollRef,
    estimateSize: () => SIZE_TO_HEIGHT[size],
    overscan: 5,
  });

  return (
    <Listbox
      as="div"
      ref={ref}
      disabled={disabled}
      className={clsx("inline-block", className)}
      multiple
      value={selectedValues}
      onChange={(vals) => {
        const nonDisabledValues = nonDisabledOptions.map((e) => e.value);
        const newValues = vals.filter((e) => e !== SELECT_ALL_VALUE);

        if (vals.includes(SELECT_ALL_VALUE as T)) {
          if (newValues.length === nonDisabledValues.length) {
            onChange([]);
          } else {
            onChange(nonDisabledValues);
          }
        } else {
          onChange(newValues);
        }
      }}
    >
      <ListboxButton
        as={SelectButton}
        className={buttonClassName}
        size={size}
        hideCaret={hideCaret}
        placeholder={placeholder}
        value={
          selectedOptions.length > 0
            ? selectedOptions.map((e) => e.label).join(", ")
            : undefined
        }
        onClear={clearable ? () => onChange([]) : undefined}
      />
      <ListboxOptions
        as={Dropdown}
        ref={setScrollRef}
        anchor={{ to: "bottom start", gap: 4 }}
        transition
        className={dropdownClassName}
      >
        {hasSearch && (
          <div className="sticky top-0 z-10 bg-white p-2 ">
            <TextInput
              placeholder="Search..."
              value={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
              wrapperClassName="w-full"
            />
          </div>
        )}
        <div
          className="relative"
          style={{
            height: `${virtualizer.getTotalSize()}px`,
          }}
        >
          {hasSelectAll && (
            <RowItem
              size={size}
              option={{ value: SELECT_ALL_VALUE, label: "Select All" }}
              start={0}
              checked={selectedValues.length === nonDisabledOptions.length}
              className={itemClassName}
            />
          )}
          {virtualizer.getVirtualItems().map((vItem) => {
            const option = filteredOptions[vItem.index];

            return (
              <RowItem
                key={String(option.value)}
                size={size}
                option={option}
                start={vItem.start}
                checked={selectedValues.includes(option.value)}
                className={itemClassName}
              />
            );
          })}
        </div>
      </ListboxOptions>
    </Listbox>
  );
};

export const RowItem = <T,>({
  option,
  checked,
  start,
  size = "sm",
  className,
}: {
  option: Option<T>;
  checked: boolean;
  start: number;
  size: BaseProps<T>["size"];
  className?: string;
}) => {
  return (
    <ListboxOption
      as={OptionItem}
      size={size}
      disabled={option.disabled}
      value={option.value}
      option={{
        ...option,
        checkbox: (
          <Checkbox
            size="sm"
            disabled={option.disabled}
            readOnly
            checked={checked}
          />
        ),
      }}
      className={twMerge(
        "data-[selected]:data-[focus]:bg-v2-action-tertiary",
        "data-[selected]:bg-v2-primary",
        className,
      )}
      style={{
        transform: `translateY(${start}px)`,
      }}
    />
  );
};

// Workaround to correctly infer generic type for forward-reffed component
export const MultiSelect = forwardRef(_MultiSelect) as <T extends string>(
  props: Props<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof _MultiSelect>;
